import { PocAssistiveText } from 'commons/components/components-wrapper';
import { InputError, InputValidity, InputWarning, ValidationCustomMessages } from '@one/web-components';
import { useTranslation } from 'core/i18n/i18n';
import { CustomValidationCode } from 'core/utils/backend-service';

export type ExtendedValidationMessages = ValidationCustomMessages & {
    error?: Partial<Record<InputError | CustomValidationCode, string>>;
    warning?: Partial<Record<InputWarning | CustomValidationCode, string>>;
};

const ERROR_KEYS = [
    'type-mismatch',
    'value-missing',
    'pattern-mismatch',
    'too-short',
    'too-long',
    'range-overflow',
    'range-underflow',
    'custom',
];

export const AssistiveText: React.FC<{
    validity?: InputValidity;
    validationMessages?: ExtendedValidationMessages;
    customValidationCode?: CustomValidationCode | InputError;
    helperText?: string;
}> = ({ validity, validationMessages = {}, customValidationCode, helperText }) => {
    const { t } = useTranslation();

    const DEFAULT_MESSAGES = {
        error: Object.fromEntries(ERROR_KEYS.map((key) => [key, t(`common.forms.errors.${key}`)])),
    };

    const mergedMessages = {
        error: {
            ...DEFAULT_MESSAGES.error,
            ...validationMessages?.error,
        },
        warning: {
            ...validationMessages?.warning,
        },
    };

    const { state, type } = validity || {};

    if (state === 'error') {
        const [errorType] = type ?? [];
        const key = errorType === 'custom' && customValidationCode ? customValidationCode : errorType;
        return (
            <PocAssistiveText status="alarm" wrap>
                {mergedMessages.error?.[key]}
            </PocAssistiveText>
        );
    }

    if (state === 'warning' && customValidationCode) {
        return (
            <PocAssistiveText status="warning" wrap>
                {mergedMessages.warning?.[customValidationCode as InputWarning | CustomValidationCode]}
            </PocAssistiveText>
        );
    }

    if (helperText) {
        return <PocAssistiveText wrap>{helperText}</PocAssistiveText>;
    }

    return undefined;
};
